import type { ManagementLevel, UsageWindowDays } from '@/services/org-admin';

/*
 * The navigation model lives in `adminTabs.ts` — it owns the tab list, the People sub-sections and
 * the compatibility map that keeps pre-restructure `?tab=` links working. Re-exported here because
 * this is the module's existing type entry point and every panel already imports from it.
 */
export type { OrgAdminPeopleSection, OrgAdminTab } from './adminTabs';

export interface OrgAdminSelfContext {
  currentUserId: string | null;
  currentUserRole: ManagementLevel | null;
  /**
   * The organization every panel below is describing.
   *
   * Server state is already keyed by this through the query keys; what a panel needs it for is its
   * own *local* state. Anything a panel remembers about the org on screen — a "payment is settling"
   * banner, a set of seats whose cancellation was just scheduled — is about THIS org and has to be
   * dropped when the org changes. Keying that reset on some query's `organization_id` instead only
   * works while that query is succeeding: if it errors, the key never changes and the previous org's
   * state stays on screen.
   */
  organizationId: string | null;
  /**
   * Whether this actor may mutate anything in the panel.
   *
   * The roles, members and invitations REST surfaces all authorise `owner` or `admin` and 403
   * everyone else (docs/admin-pannel/rbac-roles-api.md). Derived once in OrgAdminLayout so every
   * panel disables the same set of actions for the same reason, instead of each panel re-deriving
   * it — which is how the old panel ended up disabling "Invite member" but not "New role".
   */
  canManage: boolean;
}

/** Explains a disabled mutation to someone who lacks the authority for it. */
export const NO_PERMISSION_REASON = 'Only an org owner or admin can change this';

export type { UsageWindowDays };
